import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Stack,
  Text,
  PrimaryButton,
  DefaultButton,
  MessageBar,
  MessageBarType,
  Spinner,
  TextField,
  Separator,
  IconButton,
  Persona,
  PersonaSize,
  Callout,
  DirectionalHint,
  Icon,
} from '@fluentui/react';
import { IBestellpositionKommentar } from '../../shared/models/IKommentar';
import { kommentareApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

interface IPositionKommentareProps {
  bestellungId: number;
  positionId: number;
  posID: number;
  onKommentarChange?: () => void;
}

interface IUserSuggestion {
  id: string;
  displayName: string;
  mail: string;
}

// Thread-Struktur
interface IKommentarThread {
  hauptKommentar: IBestellpositionKommentar;
  antworten: IBestellpositionKommentar[];
}

export const PositionKommentare: React.FC<IPositionKommentareProps> = ({
  bestellungId,
  positionId,
  posID,
  onKommentarChange,
}) => {
  const { user } = useAuth();
  const [threads, setThreads] = useState<IKommentarThread[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [neuerKommentar, setNeuerKommentar] = useState('');
  const [saving, setSaving] = useState(false);
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [emailToNameMap, setEmailToNameMap] = useState<Map<string, string>>(new Map());

  // @-Mention Zustand
  const [showUserSuggestions, setShowUserSuggestions] = useState(false);
  const [userSuggestions, setUserSuggestions] = useState<IUserSuggestion[]>([]);
  const [mentionSearch, setMentionSearch] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const calloutTargetRef = useRef<HTMLDivElement>(null);
  // Speichere erwähnte Benutzer direkt beim Auswählen (nicht aus Text extrahieren!)
  const [selectedMentions, setSelectedMentions] = useState<Map<string, IUserSuggestion>>(new Map());

  useEffect(() => {
    loadKommentare();
  }, [positionId]);

  const loadKommentare = async () => {
    try {
      setLoading(true);
      setError(null);

      const kommentare = await kommentareApi.getPositionKommentare(positionId);

      // Thread-Struktur aufbauen
      const hauptKommentare = kommentare.filter(
        (k) => !k.ParentKommentarID || k.ParentKommentarID === ''
      );
      const antworten = kommentare.filter(
        (k) => k.ParentKommentarID && k.ParentKommentarID !== ''
      );

      const threadList: IKommentarThread[] = hauptKommentare.map((haupt) => ({
        hauptKommentar: haupt,
        antworten: antworten
          .filter((a) => a.ParentKommentarID === haupt.id)
          .sort((a, b) => {
            const dateA = a.Created ? new Date(a.Created).getTime() : 0;
            const dateB = b.Created ? new Date(b.Created).getTime() : 0;
            return dateA - dateB;
          }),
      }));

      // Neueste Threads oben
      threadList.sort((a, b) => {
        const getNewestDate = (thread: IKommentarThread): number => {
          const hauptDate = thread.hauptKommentar.Created
            ? new Date(thread.hauptKommentar.Created).getTime()
            : 0;
          const lastAntwortDate =
            thread.antworten.length > 0
              ? new Date(thread.antworten[thread.antworten.length - 1].Created || 0).getTime()
              : 0;
          return Math.max(hauptDate, lastAntwortDate);
        };
        return getNewestDate(b) - getNewestDate(a);
      });

      setThreads(threadList);

      // Lade DisplayNames für alle erwähnten Personen (Emails)
      const allEmails = new Set<string>();
      kommentare.forEach(k => {
        if (k.ErwaehntPersonen) {
          k.ErwaehntPersonen.forEach(email => allEmails.add(email));
        }
      });

      if (allEmails.size > 0) {
        const emailMap = await convertEmailsToDisplayNames(Array.from(allEmails));
        setEmailToNameMap(emailMap);
      }
    } catch (err: any) {
      setError(err.message || 'Fehler beim Laden der Kommentare');
    } finally {
      setLoading(false);
    }
  };

  // Benutzersuche für @-Mentions
  const searchUsers = useCallback(async (searchTerm: string) => {
    if (searchTerm.length < 2) {
      setUserSuggestions([]);
      return;
    }

    try {
      const response = await axios.get(`/api/users/search?q=${encodeURIComponent(searchTerm)}`);
      setUserSuggestions(response.data || []);
    } catch (err) {
      console.error('Fehler bei Benutzersuche:', err);
      setUserSuggestions([]);
    }
  }, []);

  useEffect(() => {
    if (mentionSearch.length >= 2) {
      const timer = setTimeout(() => searchUsers(mentionSearch), 300);
      return () => clearTimeout(timer);
    } else {
      setUserSuggestions([]);
      return undefined;
    }
  }, [mentionSearch, searchUsers]);

  const handleTextChange = (value: string) => {
    setNeuerKommentar(value);

    const lastAtIndex = value.lastIndexOf('@');
    if (lastAtIndex !== -1) {
      const textAfterAt = value.substring(lastAtIndex + 1);
      if (!textAfterAt.includes(' ') && textAfterAt.length >= 0) {
        setMentionSearch(textAfterAt);
        setShowUserSuggestions(true);
        setCursorPosition(lastAtIndex);
      } else {
        setShowUserSuggestions(false);
      }
    } else {
      setShowUserSuggestions(false);
    }
  };

  const handleSelectUser = (user: IUserSuggestion) => {
    const beforeAt = neuerKommentar.substring(0, cursorPosition);
    const afterMention = neuerKommentar.substring(cursorPosition + mentionSearch.length + 1);
    const newText = `${beforeAt}@${user.displayName} ${afterMention}`;
    setNeuerKommentar(newText);

    // WICHTIG: Speichere den User direkt in selectedMentions
    const newMentions = new Map(selectedMentions);
    newMentions.set(user.displayName, user);
    setSelectedMentions(newMentions);
    console.log(`[Mention] User ausgewählt: ${user.displayName} -> ${user.mail}`);

    setShowUserSuggestions(false);
    setMentionSearch('');
  };

  // Extrahiere erwähnte Personen aus dem Kommentartext
  // Format: @Nachname, Vorname oder @IT-Support
  const extractMentions = (text: string): string[] => {
    // Regex für verschiedene Namensformate:
    // - "IT-Support" (mit Bindestrich)
    // - "Nachname, Vorname" (mit Komma + Leerzeichen)
    // Stoppt bei Leerzeichen, das nicht nach Komma kommt
    const mentionRegex = /@([\wÄÖÜäöüß\-]+(?:,\s*[\wÄÖÜäöüß\-]+)?)(?=\s|$|@|:|\.|,|!|\?)/g;
    const mentions: string[] = [];
    let match;
    while ((match = mentionRegex.exec(text)) !== null) {
      mentions.push(match[1].trim());
    }
    return mentions;
  };

  // Konvertiere DisplayNames zu Email-Adressen
  const convertMentionsToEmails = async (displayNames: string[]): Promise<string[]> => {
    const emails: string[] = [];

    for (const displayName of displayNames) {
      try {
        // Suche Benutzer nach DisplayName
        const response = await axios.get(`/api/users/search?q=${encodeURIComponent(displayName)}`);

        // Finde exakte Übereinstimmung (case-insensitive)
        const user = response.data.find(
          (u: IUserSuggestion) => u.displayName.toLowerCase() === displayName.toLowerCase()
        );

        if (user && user.mail) {
          emails.push(user.mail);
          console.log(`[Mention] "${displayName}" -> ${user.mail}`);
        } else {
          console.warn(`[Mention] Kein Benutzer gefunden für: "${displayName}"`);
        }
      } catch (error) {
        console.error(`[Mention] Fehler beim Laden von Benutzer "${displayName}":`, error);
      }
    }

    return emails;
  };

  const handleSaveKommentar = async () => {
    if (!neuerKommentar.trim()) {
      setError('Bitte geben Sie einen Kommentar ein');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // NEUE LOGIK: Verwende direkt die ausgewählten Mentions (User-Objekte bereits vorhanden!)
      const mentionEmails: string[] = [];

      // Hole E-Mails aus selectedMentions (User die aus Dropdown ausgewählt wurden)
      for (const [displayName, user] of selectedMentions.entries()) {
        console.log(`[Mention] Verwende ausgewählten User: ${displayName} -> ${user.mail}`);

        // Prüfe ob es eine Gruppe ist
        if ((user as any).isGroup) {
          console.log(`[Mention] "${displayName}" ist eine Gruppe, lade Mitglieder...`);
          try {
            const membersResponse = await axios.get(`/api/users/groups/${encodeURIComponent((user as any).groupName || displayName)}/members`);
            for (const member of membersResponse.data) {
              if (member.mail && !mentionEmails.includes(member.mail)) {
                mentionEmails.push(member.mail);
                console.log(`[Mention] Gruppenmitglied: ${member.displayName} -> ${member.mail}`);
              }
            }
          } catch (groupError) {
            console.error(`[Mention] Fehler beim Laden der Gruppenmitglieder für "${displayName}":`, groupError);
          }
        } else if (user.mail && !mentionEmails.includes(user.mail)) {
          mentionEmails.push(user.mail);
        }
      }

      console.log(`[Mention] ${mentionEmails.length} E-Mail-Adressen aus ausgewählten Mentions:`, mentionEmails);

      await kommentareApi.createPositionKommentar(
        bestellungId,
        positionId,
        posID,
        neuerKommentar,
        user?.email || '', // Author Email
        mentionEmails.length > 0 ? mentionEmails : undefined,
        replyTo || undefined
      );

      setNeuerKommentar('');
      setReplyTo(null);
      setSelectedMentions(new Map()); // Zurücksetzen der ausgewählten Mentions
      await loadKommentare();
      if (onKommentarChange) {
        onKommentarChange();
      }
    } catch (err: any) {
      setError(err.message || 'Fehler beim Speichern des Kommentars');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteKommentar = async (kommentarId: string) => {
    if (!window.confirm('Möchten Sie diesen Kommentar wirklich löschen?')) {
      return;
    }

    try {
      setError(null);
      // Positions-Kommentare sind immer vom Typ 'position'
      await kommentareApi.deleteKommentar(parseInt(kommentarId, 10), 'position');
      await loadKommentare();
      if (onKommentarChange) {
        onKommentarChange();
      }
    } catch (err: any) {
      setError(err.message || 'Fehler beim Löschen des Kommentars');
    }
  };

  // Extrahiere Text aus SharePoint HTML (entfernt div-Tags)
  const extractTextFromHtml = (html: string): string => {
    if (!html) return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  // Konvertiere Email-Adressen zurück zu DisplayNames
  const convertEmailsToDisplayNames = async (emails: string[]): Promise<Map<string, string>> => {
    const emailToNameMap = new Map<string, string>();

    for (const email of emails) {
      try {
        const response = await axios.get(`/api/users/search?q=${encodeURIComponent(email)}`);
        const user = response.data.find(
          (u: IUserSuggestion) => u.mail && u.mail.toLowerCase() === email.toLowerCase()
        );

        if (user && user.displayName) {
          emailToNameMap.set(email, user.displayName);
        }
      } catch (error) {
        console.error(`[Mention] Fehler beim Laden von Benutzer "${email}":`, error);
      }
    }

    return emailToNameMap;
  };

  // Rendere erwähnte Personen als SharePoint-style Badges
  const renderMentionedPersons = (kommentar: IBestellpositionKommentar) => {
    if (!kommentar.ErwaehntPersonen || kommentar.ErwaehntPersonen.length === 0) {
      return null;
    }

    return (
      <Stack
        horizontal
        tokens={{ childrenGap: 6 }}
        wrap
        styles={{ root: { marginBottom: 8 } }}
      >
        {kommentar.ErwaehntPersonen.map((email) => {
          const displayName = emailToNameMap.get(email) || email;
          return (
            <Stack
              key={email}
              horizontal
              tokens={{ childrenGap: 4 }}
              verticalAlign="center"
              styles={{
                root: {
                  backgroundColor: '#DEECF9',
                  border: '1px solid #0078D4',
                  borderRadius: 4,
                  padding: '2px 8px',
                  display: 'inline-flex',
                },
              }}
            >
              <Icon
                iconName="Contact"
                styles={{ root: { color: '#0078D4', fontSize: 12 } }}
              />
              <Text
                variant="small"
                styles={{
                  root: {
                    color: '#0078D4',
                    fontWeight: 500,
                  },
                }}
              >
                {displayName}
              </Text>
            </Stack>
          );
        })}
      </Stack>
    );
  };

  // Rendere Kommentartext mit Mention-Highlighting
  const renderKommentarText = (text: string, mentions?: string[]) => {
    let plainText = extractTextFromHtml(text);

    // Entferne Author-Präfix am Anfang (z.B. "@IT-Support:" oder "@Schumacher, Sven:")
    plainText = plainText.replace(/^@[\wÄÖÜäöüß\-]+(?:[\s,][\wÄÖÜäöüß\-]+)*:\s*/, '');

    if (!mentions || mentions.length === 0) {
      return <span>{plainText}</span>;
    }

    // Regex für @-Mentions im Text
    const mentionRegex = /@([\wÄÖÜäöüß\-]+(?:[\s,][\wÄÖÜäöüß\-]+)*)(?=\s*[\.\!\?\n\r]|\s{2,}|$)/g;

    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match;

    while ((match = mentionRegex.exec(plainText)) !== null) {
      const displayName = match[1].trim().replace(/,\s*$/, '');
      const matchStart = match.index;
      const matchEnd = match.index + match[0].length;

      if (matchStart > lastIndex) {
        parts.push(plainText.substring(lastIndex, matchStart));
      }

      const isValidMention = Array.from(emailToNameMap.values()).some(
        name => name.toLowerCase() === displayName.toLowerCase()
      );

      if (isValidMention) {
        parts.push(
          <span
            key={matchStart}
            style={{
              backgroundColor: '#DEECF9',
              border: '1px solid #0078D4',
              borderRadius: 3,
              padding: '1px 4px',
              color: '#0078D4',
              fontWeight: 500,
            }}
          >
            @{displayName}
          </span>
        );
      } else {
        parts.push(match[0]);
      }

      lastIndex = matchEnd;
    }

    if (lastIndex < plainText.length) {
      parts.push(plainText.substring(lastIndex));
    }

    return <>{parts}</>;
  };

  const renderKommentar = (kommentar: IBestellpositionKommentar, isReply: boolean = false) => {
    const isOwnComment = kommentar.Author === user?.email;

    return (
      <Stack
        key={kommentar.id}
        tokens={{ childrenGap: 6 }}
        styles={{
          root: {
            padding: 10,
            backgroundColor: isReply ? '#f8f8f8' : '#faf9f8',
            borderRadius: 4,
            marginLeft: isReply ? 24 : 0,
            borderLeft: isReply ? '2px solid #0078d4' : 'none',
            border: isReply ? 'none' : '1px solid #edebe9',
          },
        }}
      >
        <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
          <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
            <Persona
              text={kommentar.CreatedByUser || kommentar.AuthorName || kommentar.Author || 'Unbekannt'}
              size={PersonaSize.size24}
              hidePersonaDetails={false}
              styles={{ root: { minWidth: 0 } }}
            />
            {kommentar.Created && (
              <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                {new Date(kommentar.Created).toLocaleString('de-DE')}
              </Text>
            )}
          </Stack>
          {isOwnComment && (
            <IconButton
              iconProps={{ iconName: 'Delete' }}
              title="Kommentar löschen"
              onClick={() => handleDeleteKommentar(kommentar.id || '')}
              styles={{ root: { height: 24, width: 24 } }}
            />
          )}
        </Stack>

        {/* Erwähnte Personen als Badges */}
        {renderMentionedPersons(kommentar)}

        {/* Kommentartext mit Mention-Highlighting */}
        <Text
          styles={{
            root: {
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            },
          }}
        >
          {renderKommentarText(kommentar.Kommentartext, kommentar.ErwaehntPersonen)}
        </Text>

        {/* Antworten-Button nur bei Haupt-Kommentaren */}
        {!isReply && (
          <DefaultButton
            text="Antworten"
            iconProps={{ iconName: 'Reply' }}
            onClick={() => setReplyTo(kommentar.id || null)}
            styles={{ root: { height: 24, padding: '0 8px' } }}
          />
        )}
      </Stack>
    );
  };

  const totalKommentare = threads.reduce((sum, thread) => sum + 1 + thread.antworten.length, 0);

  return (
    <Stack tokens={{ childrenGap: 12 }}>
      <Separator />
      <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>
        Kommentare zur Position
      </Text>

      {error && (
        <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}>
          {error}
        </MessageBar>
      )}

      {/* Neuer Kommentar */}
      <Stack tokens={{ childrenGap: 8 }}>
        {replyTo && (
          <MessageBar
            messageBarType={MessageBarType.info}
            onDismiss={() => setReplyTo(null)}
            dismissButtonAriaLabel="Antwort abbrechen"
          >
            Antwort auf Kommentar...
          </MessageBar>
        )}

        <div ref={calloutTargetRef}>
          <TextField
            placeholder="Kommentar hinzufügen... (@ für Erwähnung)"
            value={neuerKommentar}
            onChange={(_, value) => handleTextChange(value || '')}
            multiline
            rows={2}
            disabled={saving}
          />
        </div>

        {showUserSuggestions && userSuggestions.length > 0 && (
          <Callout
            target={calloutTargetRef.current}
            onDismiss={() => setShowUserSuggestions(false)}
            directionalHint={DirectionalHint.bottomLeftEdge}
            isBeakVisible={false}
            styles={{ root: { padding: 0 } }}
          >
            <Stack styles={{ root: { maxHeight: 150, overflowY: 'auto' } }}>
              {userSuggestions.map((user) => (
                <Stack
                  key={user.id}
                  horizontal
                  tokens={{ childrenGap: 8, padding: 8 }}
                  verticalAlign="center"
                  styles={{
                    root: {
                      cursor: 'pointer',
                      ':hover': { backgroundColor: '#f3f2f1' },
                    },
                  }}
                  onClick={() => handleSelectUser(user)}
                >
                  <Persona text={user.displayName} secondaryText={user.mail} size={PersonaSize.size24} />
                </Stack>
              ))}
            </Stack>
          </Callout>
        )}

        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <PrimaryButton
            text={replyTo ? 'Antworten' : 'Kommentieren'}
            onClick={handleSaveKommentar}
            disabled={saving || !neuerKommentar.trim()}
            styles={{ root: { height: 28 } }}
          />
          {replyTo && (
            <DefaultButton
              text="Abbrechen"
              onClick={() => {
                setReplyTo(null);
                setNeuerKommentar('');
                setSelectedMentions(new Map()); // Zurücksetzen der ausgewählten Mentions
              }}
              disabled={saving}
              styles={{ root: { height: 28 } }}
            />
          )}
        </Stack>
      </Stack>

      {/* Kommentare anzeigen */}
      {loading ? (
        <Spinner label="Lade Kommentare..." />
      ) : threads.length === 0 ? (
        <Text variant="small" styles={{ root: { color: '#605e5c', fontStyle: 'italic' } }}>
          Noch keine Kommentare vorhanden.
        </Text>
      ) : (
        <Stack tokens={{ childrenGap: 8 }}>
          <Text variant="small">
            {totalKommentare} {totalKommentare === 1 ? 'Kommentar' : 'Kommentare'}
          </Text>
          {threads.map((thread) => (
            <Stack key={thread.hauptKommentar.id} tokens={{ childrenGap: 6 }}>
              {renderKommentar(thread.hauptKommentar, false)}
              {thread.antworten.map((antwort) => renderKommentar(antwort, true))}
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );
};
